/**
 * Email verification before a company signup: the first submit emails a
 * 6-digit code (Org/Public/Signup/EmailCode/), the next one sends it with the
 * signup. Environments that don't require a code skip straight to signup.
 */
import { useState } from 'react'
import { toast } from 'sonner'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getErrorMessage } from '@/lib/api'
import { authApi } from '@/services/auth'

export function useSignupEmailCode() {
  const [sentTo, setSentTo] = useState('')
  const [code, setCode] = useState('')
  const [sending, setSending] = useState(false)

  const isAwaiting = (email: string) => Boolean(sentTo) && sentTo === email.trim().toLowerCase()

  /** Emails a code. Resolves true when no code is needed and signup can go ahead. */
  const send = async (email: string): Promise<boolean> => {
    setSending(true)
    try {
      const { required } = await authApi.requestSignupEmailCode(email.trim())
      if (!required) return true
      setSentTo(email.trim().toLowerCase())
      setCode('')
      toast.success(`We've emailed a 6-digit code to ${email.trim()}.`)
      return false
    } catch (error) {
      toast.error(getErrorMessage(error))
      return false
    } finally {
      setSending(false)
    }
  }

  /**
   * Call at the start of submit. Returns the code to send with the signup,
   * '' when none is needed, or null when signup must wait (code just sent,
   * or the field is incomplete).
   */
  const prepare = async (email: string): Promise<string | null> => {
    if (!isAwaiting(email)) return (await send(email)) ? '' : null
    if (!/^\d{6}$/.test(code)) {
      toast.error('Enter the 6-digit code we emailed you.')
      return null
    }
    return code
  }

  return { sentTo, code, setCode, sending, isAwaiting, send, prepare }
}

export function SignupEmailCodeField({
  emailCode,
  email,
  disabled,
}: {
  emailCode: ReturnType<typeof useSignupEmailCode>
  email: string
  disabled?: boolean
}) {
  if (!emailCode.isAwaiting(email)) return null
  return (
    <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
      <Label htmlFor="signup-email-code">Verification code</Label>
      <Input
        id="signup-email-code"
        value={emailCode.code}
        onChange={(event) => emailCode.setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="6-digit code"
        maxLength={6}
        disabled={disabled}
      />
      <p className="text-xs text-muted-foreground">
        Sent to {emailCode.sentTo}. Didn’t get it? Check spam, or{' '}
        <button
          type="button"
          className="font-medium text-primary underline disabled:opacity-50"
          onClick={() => void emailCode.send(email)}
          disabled={disabled || emailCode.sending}
        >
          send a new code
        </button>
        .
      </p>
    </div>
  )
}
